import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import Modal from "../../components/Modal";
import {callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb} from 'common/appInterface';
import {CardProductCode} from "../../constants/Card";
import {SuccessResponseCodeList} from "../../constants/Common";

const DeliveryInputPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL14";
  const screenName = t("APPL14.title");

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep6>>({ ...location.state.apply, thirdPartyReceiptYn: "Y" });
  const [isModal, setModal] = useState(false);
  const [isClick, setClick] = useState(false);
  
  const [displayCardIssueFee, setDisplayCardIssueFee] = useState<number | null>(null);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 모달 닫기 */
  const closeModal = useCallback(() => {
    setModal(false);
  }, []);
	
  /**
   * dhbyun
   * 표기 카드 발급비 조회
   */
	const inquiryDisplayCardIssueFeeApi = useCallback(
		async () => {
			await client.get(`/api/app/apply/${cardApply.cardProductCode}/issueFee`, { params: location.state.query }).then((response) => {
				if(response.data.response) {
					setDisplayCardIssueFee(response.data.response.cardIssueFee);
				}
			}).catch((e: AxiosError) => {
				callAlertWeb("ERROR", "99", e.message);
			});
		},
		[cardApply.cardProductCode, location]
	);
  
  /** Next 처리 */
  const clickNext = useCallback(async () => {
    if (isClick) {
      console.log("Already Clicked...");
      return;
    }

    setClick(true);
    showProgressWeb();

    // 주소검증
    await client.get("/api/app/apply/verify/address", { params: { ...location.state.query, homeZipCode: cardApply.homeZipCode, homeNewAddress: cardApply.homeNewAddress, homeNewDetailAddress: cardApply.homeNewDetailAddress }}).then((response) => {
      if (response.data.code === "00") {
        setModal(true);
      }
      else {
        alert(response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      setClick(false);
    });
  }, [isClick, location.state.query, cardApply.homeZipCode, cardApply.homeNewAddress, cardApply.homeNewDetailAddress]);

  /** 주소변경 처리 */
  const clickChangeAddress = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 카드신청정보 확인완료 */
	const clickModalYes = useCallback(async () => {
		showProgressWeb();
		
		// 카드신청내역 등록
		await client.patch("/api/app/apply/history", {
			...location.state.query,
			receiveSeq: cardApply.receiveSeq,
			receiveDate: cardApply.receiveDate,
			receiveStatusCode: "07",
			sendTypeDivisionCode: location.state.apply.receiveMethod === "01"? "1": "3", // 카드 수령 방법이 직접 수령(01)인 경우 본인 수령 "1", 카드 수령 방법이 우편함 배송(02)인 경우 지정영업점배송 "3"
			deliverySelectCode: cardApply.deliverySelectCode,
			homeZipCode: cardApply.homeZipCode,
			homeNewAddress: cardApply.homeNewAddress,
			homeNewDetailAddress: cardApply.homeNewDetailAddress,
			homeEngAddress: cardApply.homeEngAddress,
			homeRoadManagementNo: cardApply.homeRoadManagementNo,
			thirdPartyReceiptYn: cardApply.thirdPartyReceiptYn,
			cardProductCode: cardApply.cardProductCode,
      addressChangeYn : cardApply.isChangeDelivery ? "Y" : "N",
      receiveMethod: location.state.apply.receiveMethod
		}).then(async (response) => {
			if (response.data.code === "00") {
				// 수령지변경은 메인으로 이동
				if (cardApply.isChangeDelivery) {
					navigate("/main", {
						state: {
							query: location.state.query,
							swiperIndex: location.state.swiperIndex,
							tabIndex: location.state.tabIndex
						}
					});
				} else {
					switch (Number(cardApply.cardProductCode)) {
						case CardProductCode.DreamCard:
						case CardProductCode.PremierCard:
							navigate("/apply/step7", {
								state: {
									query: location.state.query
									, apply: {
										isChangeDelivery: false
										, callPage: 'cardApply'
										, cardApply: cardApply
									}
								}
							});
							break;
						case CardProductCode.WelcomeCard:
							if (cardApply.requestIssueDivisionCode?.startsWith("2") || cardApply.requestIssueDivisionCode?.startsWith("3")) {
								navigate("/apply/complet", {state: {...location.state, apply: cardApply}});
							} else {
								await client.post("/api/app/apply/chargeCardIssueFee", {
									...location.state.query,
									receiveSeq: cardApply.receiveSeq,
									receiveDate: cardApply.receiveDate
								}).then((res) => {
									if (SuccessResponseCodeList.includes(res.data.code)) {
										if (res.data.response.bCharged === true) {
											navigate("/apply/complet", {state: {...location.state, apply: cardApply}});
										} else {
											navigate("/apply/depositCardIssuanceFee", {
												state: {
													...location.state,
													apply: cardApply,
													storage: {
														...location.state.storage,
														chargeCardIssueFeeRes: res.data.response,
														displayCardIssueFee: displayCardIssueFee
													}
												}
											});
										}
									} else {
										callAlertWeb("ERROR", res.data.code, res.data.message);
									}
								}).catch((e: AxiosError) => {
									callAlertWeb("ERROR", "99", e.message);
								});
							}
							break;
						case CardProductCode.DirectCard:
						default:
							navigate("/apply/complet", {state: {...location.state, apply: cardApply}});
							break;
					}
				}
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location, navigate, cardApply, displayCardIssueFee]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);
	  
	inquiryDisplayCardIssueFeeApi();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents pb64-mb">
          <div className="singUpWrapSub">
            <div className="inputBoxTy01">
              <p className="label">{t("APPL14.header01")}</p>
              <p className="addressNum">{cardApply.homeZipCode}</p>
              <p className="address">{cardApply.homeEngAddress}</p>
              <button type="button" className="add_btn" onClick={clickChangeAddress}>{t("APPL14.button01")}</button>
            </div>
            <div className="inputBoxTy01 mgt50"> 
              <label>
                <input placeholder=" " type="text" value={cardApply.homeNewDetailAddress} onChange={(e) => setCardApply({ ...cardApply, homeNewDetailAddress: e.target.value })} />
                <span>{t("APPL14.header02")}</span>
                <p className="message">{t("APPL14.explan01")}</p>
              </label>
            </div>
            {/*<div className="checkbox">
              <div className="checkboxInner">
                <input type="checkbox" id="agree03" name="" checked={cardApply.thirdPartyReceiptYn === "Y"} onChange={(e) => setCardApply({ ...cardApply, thirdPartyReceiptYn: e.target.checked ? "Y" : "N" })} />
                <label htmlFor="agree03">
                  <span>{t("APPL14.explan02")}</span>
                </label>
              </div>
            </div>*/}
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={isClick} onClick={clickNext}><span>{t("COMMON.button.next")}</span></button>
          </div>
        </div>
      </div>
      <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
        <p className="message">{t("APPL14.explan04")}</p>
        <dl>
          {/*<dt>{t("APPL14.header03")}</dt>
          <dd>{cardApply.userEngName}</dd>*/}
          <dt>{t("APPL14.header04")}</dt>
          <dd><span>({cardApply.homeZipCode}) {cardApply.homeEngAddress}</span></dd>
          <dd className="detail">{cardApply.homeNewDetailAddress}</dd>
        </dl>
        <div className="btnWrapTy02">
          <button type="button" className="btnPrimaryTy03" onClick={closeModal}><span>{t("COMMON.button.no")}</span></button>
          <button type="button" className="btnPrimaryTy01" onClick={clickModalYes}><span>{t("COMMON.button.yes")}</span></button>
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryInputPage;